<template>
  <lazy-component class="video-lazy-wrapper" :class="[ratio ? `ratio ratio-${ratio}` : '', { 'full-height': fullHeight }]" @intersected="onShow">
    <div ref="videoWrapper" class="video-wrapper" :class="{ initialized: !!player }" @click="play" @touchstart="play">
      <video
        :id="`video-container-${id}`"
        ref="videoPlayer"
        class="video-js"
        :class="{ 'no-preview': !hasPreview }"
        preload="auto"
        :poster="poster"
        crossorigin="anonymous"
        playsinline
        :muted="muted"
      >
        <source v-if="videoSource && isNativeHls" :src="videoSource" :type="videoSourceType" />
      </video>

      <slot />
    </div>
  </lazy-component>
</template>

<script setup lang="ts">
import { computed, nextTick, onBeforeMount, onBeforeUnmount, ref, watch } from "vue";
import { useUserStore } from "@/store/user";
import { useVideoStore } from "@/store/video";
import { usePlayerStore } from "@/store/player";
import { getMediaUrl } from "@/utils/mediaHelper";
import { amplitudeVideoTrack } from "@/utils/amplitude";
import { ENV_PRODUCTION, ROLE_CENSORED_TOKEN } from "@/utils/variables";
import videojs from "video.js";
import "videojs-mux";
import "videojs-mobile-ui";
import "videojs-hotkeys";
import { insertScript } from "@/utils/domHelper";
import seekButtons from "@/utils/playerHelpers/seekButtons";
import qualitySelector from "@/utils/playerHelpers/qualitySelector";
import { useI18n } from "vue-i18n";
import { useRoleStore } from "@/store/role.ts";
import { getCensoredVideoSource } from "@/utils/censorHelper.ts";
import { useRoute } from "vue-router";

defineOptions({
  name: "player",
});

const props = defineProps({
  id: {
    type: [Number, String],
    required: false,
    default: null,
  },
  autoplay: {
    type: Boolean,
    required: false,
    default: false,
  },
  source: {
    type: String,
    required: false,
    default: null,
  },
  sourceType: {
    type: String,
    required: false,
    default: "application/x-mpegURL",
  },
  poster: {
    type: String,
    default: null,
  },
  subtitlesEn: {
    type: [String, null],
    required: false,
    default: null,
  },
  subtitlesDe: {
    type: [String, null],
    required: false,
    default: null,
  },
  subtitlesFr: {
    type: [String, null],
    required: false,
    default: null,
  },
  ratio: {
    type: String,
    default: "",
    validator(value: string) {
      return ["", "16x9", "4x3"].includes(value);
    },
  },
  fullHeight: {
    type: Boolean,
    required: false,
    default: false,
  },
  hasPreview: {
    type: Boolean,
    required: false,
    default: false,
  },
  trackPlayback: {
    type: Boolean,
    required: false,
    default: false,
  },
  cast: {
    type: Boolean,
    required: false,
    default: true,
  },
});

declare global {
  interface Window {
    cast?: any;
    chrome?: any;
    videojs?: any;
  }
}

const { locale } = useI18n();

const emit = defineEmits(["play", "ready", "sendPlayback", "show"]);
const userStore = useUserStore();
const videoStore = useVideoStore();
const playerStore = usePlayerStore();
const roleStore = useRoleStore();
const route = useRoute();

const videoWrapper = ref<HTMLElement | null>(null);
const videoPlayer = ref<HTMLElement | null>(null);
const player = ref<any>(null);
const currentTextTrackIndex = ref<number | null>(null);
const started = ref(false);
/** Same-origin blob URLs for iOS native <track> (revoked on dispose). */
const subtitleBlobUrls = ref<string[]>([]);
const log = ref({
  timerStart: 0,
  currentTime: 0,
  previousTime: 0,
  playbackSent: false,
});

const activeVideo = computed(() => videoStore.getActiveVideo);
const profile = computed(() => userStore.getProfile);
const volume = computed(() => playerStore.getVolume);
const muted = computed(() => !!route.query?.ts);

// Safari/iOS play HLS natively via the <source> child; other browsers go through VHS
const isNativeHls = videojs.browser.IS_IOS || videojs.browser.IS_ANY_SAFARI;

const isCensored = computed(() => roleStore.hasRole(ROLE_CENSORED_TOKEN));
const videoSource = computed(() => (isCensored.value ? getCensoredVideoSource() : props.source));
// Safari 27 is stricter about <source type> vs CDN Content-Type; our HLS CDN serves
// application/vnd.apple.mpegurl while we historically declared application/x-mpegURL.
const videoSourceType = computed(() => {
  const declared = isCensored.value ? "application/x-mpegURL" : props.sourceType;
  const normalized = (declared || "").toLowerCase();
  const isHls = normalized === "application/x-mpegurl" || normalized === "application/vnd.apple.mpegurl";

  if (isHls && (videojs.browser.IS_ANY_SAFARI || videojs.browser.IS_IOS)) {
    return "application/vnd.apple.mpegurl";
  }

  return declared;
});

const subtitles = computed(() => {
  const subtitles: Record<string, any>[] = [];

  if (props.subtitlesEn) {
    subtitles.push({ label: "English", lang: "en", src: getSubtitles(props.subtitlesEn) });
  }

  if (props.subtitlesFr) {
    subtitles.push({ label: "French", lang: "fr", src: getSubtitles(props.subtitlesFr) });
  }

  if (props.subtitlesDe) {
    subtitles.push({ label: "Deutsch", lang: "de", src: getSubtitles(props.subtitlesDe) });
  }

  return subtitles;
});

defineExpose({
  log,
  subtitles,
  currentTextTrackIndex,
});

watch(
  () => props.poster,
  (newPoster) => {
    if (player.value) {
      player.value.poster(newPoster);
    }
  },
);

watch(activeVideo, (newVal, oldVal) => {
  if (oldVal === props.id && player.value) {
    player.value.pause();
    sendPlayback();
  }
});

watch(currentTextTrackIndex, (val) => {
  if (!window.cast) {
    return;
  }

  const mediaSession = window.cast.framework.CastContext.getInstance().getCurrentSession()?.getMediaSession();

  if (!mediaSession) {
    return;
  }

  const tracksInfoRequest = new window.chrome.cast.media.EditTracksInfoRequest(val === null ? [] : [val]);
  mediaSession.editTracksInfo(tracksInfoRequest, null, null);
});

const play = async () => {
  if (!props.source) {
    return;
  }

  emit("play");

  if (videoPlayer.value) {
    videoPlayer.value.focus({ preventScroll: true });
  }
};

const load = () => {
  if (player.value || !videoPlayer.value) {
    return;
  }

  const seekStep = 10;
  const initTime = Date.now();
  const mediaEvents: string[] = [];
  const useOverride = !isNativeHls;
  // iOS native fullscreen only shows native cues. Do not put <track> in the template:
  // Safari fetches them during HLS load and a missing VTT 404 can fail the whole video.
  const useNativeTextTracks = videojs.browser.IS_IOS;

  let options: Record<string, any> = {
    controls: true,
    errorDisplay: false,
    disablePictureInPicture: true,
    techOrder: ["html5"],
    // Non-iOS: emulated tracks via options. iOS: blob <track> added after canplay (below).
    tracks: useNativeTextTracks
      ? []
      : subtitles.value.map((subtitle) => ({
          kind: "subtitles",
          src: subtitle.src,
          language: subtitle.lang,
          label: subtitle.label,
        })),
    plugins: {
      hotkeys: {
        volumeStep: 0.1,
        seekStep: seekStep,
        enableModifiersForNumbers: false,
        enableVolumeScroll: false,
      },
      mobileUi: {
        fullscreen: {
          enterOnRotate: true,
          lockOnRotate: !videojs.browser.IS_IOS,
          lockToLandscapeOnEnter: !videojs.browser.IS_IOS,
        },
      },
    },
    html5: {
      nativeTextTracks: useNativeTextTracks,
      nativeCaptions: useNativeTextTracks,
      // only force emulated A/V tracks when VHS overrides Safari native HLS
      ...(useOverride
        ? {
            nativeAudioTracks: false,
            nativeVideoTracks: false,
          }
        : {}),
      vhs: {
        overrideNative: useOverride,
        handlePartialData: true,
      },
    },
    controlBar: {
      pictureInPictureToggle: false,
    },
    poster: props.poster,
    // not a <source> child: if the browser picks it up natively before videojs init,
    // Html5 tech keeps native playback, VHS never attaches and no quality levels exist
    ...(isNativeHls ? {} : { sources: [{ src: videoSource.value, type: videoSourceType.value }] }),
  };

  if (videojs.getPlugin("chromecast") !== undefined && window.cast) {
    options = setChromecastOptions(options);
  }

  if (import.meta.env.VITE_MUX_ENABLED && profile?.value?.id) {
    options = setMuxOptions(options);
  }

  player.value = videojs(videoPlayer.value, options, () => {
    nextTick(() => {
      const posterImg = videoWrapper.value?.querySelector(".vjs-poster img");

      if (posterImg) {
        posterImg.setAttribute("fetchpriority", "high");
        posterImg.setAttribute("loading", "eager");
      }

      const touchOverlay = videoWrapper.value?.querySelector(".vjs-touch-overlay");

      if (touchOverlay) {
        const observer = new MutationObserver(() => {
          if (touchOverlay.classList.length === 1 && !player.value.paused()) {
            player.value.controlBar.addClass("hide");
          } else {
            player.value.controlBar.removeClass("hide");
          }
        });

        observer.observe(touchOverlay, {
          attributes: true,
          attributeFilter: ["class"],
          childList: false,
          characterData: false,
        });
      }

      seekButtons(player.value, seekStep);

      emit("ready", player.value);
    });
  });

  // Prefer UI locale before TextTrackDisplay.preselectTrack runs (player ready is async).
  // Do not set track.mode / rebuild SubsCapsButton here — that desyncs the CC menu.
  if (subtitles.value.some((subtitle) => subtitle.lang === locale.value)) {
    player.value.cache_.selectedLanguage = {
      enabled: true,
      language: locale.value,
      kind: "subtitles",
    };
  }

  player.value.on(
    [
      "loadstart",
      "loadedmetadata",
      "loadeddata",
      "canplay",
      "suspend",
      "abort",
      "emptied",
      "stalled",
      "waiting",
      "playing",
      "pause",
      "seeking",
      "ended",
      "error",
    ],
    (event: { type: string }) => {
      mediaEvents.push(`${event.type}@${Date.now() - initTime}`);

      if (mediaEvents.length > 20) {
        mediaEvents.shift();
      }
    },
  );

  player.value.one("canplay", () => {
    player.value.volume(volume.value);

    if (route.query?.ts) {
      player.value.currentTime(parseInt(route.query.ts as string));
    }
  });

  // iOS: fetch VTTs ourselves, then attach native tracks as blob URLs after media is ready.
  // Only successful fetches become <track> nodes — missing files stay off the video element.
  if (useNativeTextTracks && subtitles.value.length > 0) {
    player.value.one("canplay", async () => {
      if (!player.value) {
        return;
      }

      for (const subtitle of subtitles.value) {
        const src = String(subtitle.src);

        if (src === "") {
          continue;
        }

        try {
          const response = await fetch(src);

          if (!response.ok) {
            continue;
          }

          const blobUrl = URL.createObjectURL(new Blob([await response.text()], { type: "text/vtt" }));
          subtitleBlobUrls.value.push(blobUrl);
          player.value.addRemoteTextTrack(
            {
              kind: "subtitles",
              src: blobUrl,
              language: subtitle.lang,
              label: subtitle.label,
            },
            true,
          );
        } catch {
          // skip unreachable VTT — never append a failing native <track>
        }
      }
    });
  }

  if (player.value.airPlay !== undefined) {
    player.value.airPlay();
  }

  if (player.value.chromecast !== undefined) {
    player.value.chromecast();
  }

  player.value.one("play", async () => {
    started.value = true;
    amplitudeVideoTrack("video starts playing", props.id);
  });

  player.value.on("play", async () => {
    log.value.playbackSent = false;
    log.value.timerStart = player.value.currentTime();
    window.addEventListener("beforeunload", sendPlayback);

    videoStore.setActiveVideo(props.id);
  });

  player.value.on("texttrackchange", () => {
    const textTracks = player.value.textTracks();
    const subtitleTracks = [];

    for (let i = 0; i < textTracks.length; i++) {
      if (textTracks[i].kind !== "subtitles") {
        continue;
      }

      subtitleTracks.push(textTracks[i]);
    }

    for (let i = 0; i < subtitleTracks.length; i++) {
      if (subtitleTracks[i].mode === "showing") {
        currentTextTrackIndex.value = i;
        return;
      }
    }

    currentTextTrackIndex.value = null;
  });

  qualitySelector(player.value, videoWrapper.value, videoSource.value);

  player.value.on("timeupdate", () => {
    log.value.previousTime = log.value.currentTime;
    log.value.currentTime = player.value.currentTime();
  });

  player.value.on("volumechange", () => {
    const vlm = player.value.muted() ? 0 : parseFloat(player.value.volume());
    playerStore.setVolume(vlm);
  });

  player.value.on("seeking", () => {
    if (!log.value.playbackSent) {
      sendPlayback();
    }
  });

  player.value.on("ended", () => {
    sendPlayback();
  });

  player.value.on("error", async () => {
    if (!videoSource.value?.includes("ersties") || !profile?.value?.id) {
      return; // dont report errors from other domains or anonymous users
    }

    const error = player.value.error();
    const tech = player.value.tech({ IWillNotUseThisInApp: true });
    // raw MediaError of the underlying <video> element; on Safari its message
    // carries the AVFoundation/CoreMedia error, which videojs' message hides
    const nativeError = tech?.el_?.error;
    const msSinceInit = Date.now() - initTime;
    let headStatus: number | null = null;
    let headError: string | null = null;

    try {
      const response = await fetch(videoSource.value, { method: "HEAD", signal: AbortSignal.timeout(3000) });
      headStatus = response.status;
    } catch (fetchError: unknown) {
      headError = fetchError instanceof Error ? fetchError.message : String(fetchError);
    }

    window.bugsnag?.notify(new Error(`Video error : ${error?.code}`), (event) => {
      event.severity = "error";
      event.context = "video-player";
      event.groupingHash = `video-error-${error?.code}`;
      event.addMetadata("video", {
        id: props.id,
        source: videoSource.value,
        error: error?.message,
        userId: profile.value?.id,
        nativeErrorCode: nativeError?.code,
        nativeErrorMessage: nativeError?.message,
        msSinceInit,
        mediaEvents: mediaEvents.join(" "),
        networkState: tech?.el_?.networkState,
        readyState: tech?.el_?.readyState,
        currentTime: player.value?.currentTime(),
        connection: (navigator as any).connection?.effectiveType,
        headStatus,
        headError,
        subtitleCount: subtitles.value.length,
      });
    });
  });

  if (videojs.browser.IS_IOS && videojs.browser.IS_CHROMIUM) {
    return;
  }

  if (props.autoplay && player.value.paused()) {
    setTimeout(() => {
      player.value?.play()?.catch(() => {});
    }, 100);
  }
};

const sendPlayback = () => {
  if (!props.trackPlayback) {
    return;
  }

  if (log.value.playbackSent) {
    return;
  }

  const duration = log.value.previousTime - log.value.timerStart;

  if (Math.floor(duration) <= 0) {
    return;
  }

  emit("sendPlayback", {
    duration: duration,
    startDate: Math.floor(Date.now() / 1000),
    timelineStart: log.value.timerStart,
  });
};

const onShow = async () => {
  if (!props.source) {
    return;
  }

  (videojs.log as any).warn = function () {};

  if (!window.videojs) {
    window.videojs = videojs;
  }

  if (props.cast) {
    await insertScript(getMediaUrl("assets/js", "silvermine-videojs-airplay.min.js"));
    await insertScript(getMediaUrl("assets/js", "silvermine-videojs-chromecast.min.js"));
    await insertScript("https://www.gstatic.com/cv/js/sender/v1/cast_sender.js?loadCastFramework=1");
  }

  if (videoSourceType.value === "video/mp4") {
    await insertScript(getMediaUrl("assets/js", "videojs-vr.js"));
  }

  await nextTick();
  load();
};

const getSubtitles = (name: string) => {
  if (name === "") {
    return "";
  }

  name = `${name.split(".").slice(0, -1).join(".")}.vtt`;
  // todo: workaround for subtitles on ios due to cors problem
  const subtitleHost = import.meta.env.VITE_ENV === ENV_PRODUCTION ? location.origin : import.meta.env.VITE_MEDIA_CONTENT_HOST;
  return `${subtitleHost}/content/subtitles/${name}`;
};

const setChromecastOptions = (options: Record<string, any>) => {
  options.techOrder.unshift("chromecast");
  options.chromecast = {
    modifyLoadRequestFn: (loadRequest: any) => {
      const tracks = [];

      for (let i = 0; i < subtitles.value.length; i++) {
        const track = new window.chrome.cast.media.Track(i, window.chrome.cast.media.TrackType.TEXT);
        track.trackContentId = subtitles.value[i].src;
        track.trackContentType = "text/vtt";
        track.subtype = window.chrome.cast.media.TextTrackType.CAPTIONS;
        track.name = subtitles.value[i].label;
        track.language = subtitles.value[i].lang;
        tracks.push(track);
      }

      loadRequest.activeTrackIds = currentTextTrackIndex.value !== null ? [currentTextTrackIndex.value] : [];
      loadRequest.media.tracks = tracks;

      return loadRequest;
    },
  };
  options.plugins.chromecast = {};

  return options;
};

const setMuxOptions = (options: Record<string, any>) => {
  options.plugins.mux = {
    debug: false,
    minimumRebufferDuration: 250,
    data: {
      env_key: import.meta.env.VITE_MUX_KEY,
      viewer_user_id: profile.value?.id || "",
      video_title: props.id,
      player_init_time: Date.now(),
    },
  };

  return options;
};

onBeforeMount(() => {
  emit("show");
});
onBeforeUnmount(() => {
  window.removeEventListener("beforeunload", sendPlayback);

  sendPlayback();

  for (const url of subtitleBlobUrls.value) {
    URL.revokeObjectURL(url);
  }

  subtitleBlobUrls.value = [];

  if (!player.value) {
    return;
  }

  // keep the player alive only while casting: disposing it would end the chromecast session.
  // otherwise always dispose, even for the active video: undisposed players stay in the
  // videojs registry and hold WebKit media resources; once enough of them accumulate,
  // every new video fails instantly with MEDIA_ERR_SRC_NOT_SUPPORTED (error 4)
  let casting: boolean;

  try {
    casting = !!window.cast?.framework?.CastContext?.getInstance()?.getCurrentSession();
  } catch {
    casting = false;
  }

  if (!casting) {
    try {
      player.value.dispose();
    } catch {
      // already disposed / torn down mid-error
    }

    player.value = null;
  }
});
</script>

<style lang="scss">
@import "video.js/dist/video-js.css";
@import "videojs-mobile-ui/dist/videojs-mobile-ui.css";
@import "@/assets/silvermine/silvermine-videojs-chromecast.css";
@import "@/assets/silvermine/silvermine-videojs-airplay.css";
@import "./index";
</style>
